'use client';
// This component is supposed to run on the client only.

import { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { usePathname } from 'next/navigation';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { waitUntilReady } from '../../../../utils/onLoadTracking';

const campaignLogoShape = confetti.shapeFromPath({
  path: 'M 138.375 0 C 98.996334 0 66.958984 32.035397 66.958984 71.414062 C 66.958984 110.79273 98.996334 142.83008 138.375 142.83008 C 161.58167 142.83008 182.17323 131.65303 195.22656 114.45703 C 208.26523 131.6637 228.86057 142.83008 252.11523 142.83008 C 291.55657 142.83008 323.5293 110.85539 323.5293 71.414062 C 323.5293 31.97273 291.55657 -1.8947806e-14 252.11523 0 C 228.86057 0 208.26523 11.164428 195.22656 28.371094 C 182.17323 11.173761 161.58167 9.4739031e-15 138.375 0 z M 38.179688 0.41992188 L 0 12.539062 L 5.2519531 32.740234 L 30.705078 25.667969 L 30.705078 142.83008 L 55.353516 142.83008 L 55.353516 0.41992188 L 38.179688 0.41992188 z M 138.375 24.28125 C 158.68833 24.28125 175.9909 37.223391 182.60156 55.275391 C 181.3989 60.468724 180.70117 65.854063 180.70117 71.414062 C 180.70117 76.972729 181.3989 82.359401 182.60156 87.552734 C 175.9909 105.60473 158.68833 118.54883 138.375 118.54883 C 112.387 118.54883 91.240234 97.403395 91.240234 71.414062 C 91.240234 45.42473 112.387 24.28125 138.375 24.28125 z M 234.24414 30.248047 C 238.90281 30.248047 244.46094 33.088042 244.46094 46.609375 C 244.46094 60.136041 238.90281 62.976562 234.24414 62.976562 C 229.58414 62.976562 224.02734 60.136041 224.02734 46.609375 C 224.02734 33.088042 229.58414 30.248047 234.24414 30.248047 z M 271.03906 30.248047 C 275.69906 30.248047 281.25391 33.088042 281.25391 46.609375 C 281.25391 60.136041 275.69906 62.976562 271.03906 62.976562 C 266.3804 62.976562 260.82227 60.136041 260.82227 46.609375 C 260.82227 33.088042 266.3804 30.248047 271.03906 30.248047 z M 212.3457 76.943359 C 219.96437 82.928693 234.91123 87 252.11523 87 C 269.3179 87 284.26805 82.928693 291.88672 76.943359 C 291.75872 98.806025 274.00723 116.49219 252.11523 116.49219 C 230.22323 116.49219 212.4737 98.806025 212.3457 76.943359 z ',
  matrix: [0.07, 0, 0, 0.07, 0, 0] as unknown as DOMMatrix, // the type seems to be wrong. Using a DOMMatrix instead of an array doesn't work.
});

const heartShape = confetti.shapeFromPath({
  path: 'M167 72c19,-38 37,-56 75,-56 42,0 76,33 76,75 0,76 -76,151 -151,227 -76,-76 -151,-151 -151,-227 0,-42 33,-75 75,-75 38,0 57,18 76,56z',
  matrix: [0.03, 0, 0, 0.03, -5.57, -5.53] as unknown as DOMMatrix, // the type seems to be wrong. Using a DOMMatrix instead of an array doesn't work.
});

const starShape = confetti.shapeFromPath({
  path: 'M50,0 L61.8,35.1 L100,38.2 L69.1,61.8 L80.9,100 L50,76.6 L19.1,100 L30.9,61.8 L0,38.2 L38.2,35.1 Z',
  matrix: [0.1, 0, 0, 0.1, -5, -5] as unknown as DOMMatrix, // the type seems to be wrong. Using a DOMMatrix instead of an array doesn't work.
});
const circleShape = confetti.shapeFromPath({
  path: 'M50,0 A50,50 0 1,0 50.0001,0 Z',
  matrix: [0.05, 0, 0, 0.05, -2.5, -2.5] as unknown as DOMMatrix, // the type seems to be wrong. Using a DOMMatrix instead of an array doesn't work.
});

/**
 * Fires a fireworks-like confetti
 */
const fireworks = () => {
  const duration = 2.5 * 1000;
  const animationEnd = Date.now() + duration;
  const shapes = [circleShape, starShape, heartShape, campaignLogoShape];
  const colors = ['#FAB700', '#6B5D56', '#FDC300', '#8C8480'];
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0, scalar: 2 };

  function randomInRange(min: number, max: number) {
    return Math.random() * (max - min) + min;
  }

  const interval = setInterval(() => {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      return clearInterval(interval);
    }

    const particleCount = 40 * (timeLeft / duration);

    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      shapes,
      colors,
    });
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      shapes,
      colors,
    });
    return null;
  }, 250);
};

const day = 1000 * 60 * 60 * 24;

/**
 * Set confetti seen time to localstorage
 */
const setSeen = () => localStorage.setItem('confettiLastSeen', `${Date.now()}`);

/**
 * Determine if the confetti has been seen since the specified number of days
 */
const hasBeenSeen = (daysSince: number) => {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  // time from localStorage when the user last saw confetti
  const confettiLastSeen = parseInt(localStorage.getItem('confettiLastSeen') || '0', 10);
  return confettiLastSeen + day * daysSince > Date.now();
};

/**
 * Makes sure that the confetti only fires at the beginning of a browsing session.
 * Otherwise it would fire while navigation in the page if the pause time expires during a session.
 */
const setConfettiSessionLock = () => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem('showConfetti', 'false');
};

const isOnPageLoad = () => sessionStorage.getItem('showConfetti') !== 'false';

export const useCookieBannerClosed = () => {
  const { OptanonAlertBoxClosed } = useCookies().getCookies();
  const [isClosed, setIsClosed] = useState(Boolean(OptanonAlertBoxClosed));
  useEffect(() => {
    if (!isClosed) {
      waitUntilReady(
        () =>
          document.cookie
            .split(';')
            .some((c) => c.split('=')[0].trim() === 'OptanonAlertBoxClosed'),
        () => setIsClosed(true),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isClosed;
};

/**
 * Fires a fireworks-like confetti using the library 'canvas-confetti'.
 * The confetti only starts after after the cookie banner has been closed (either accepted or declined)
 * and only once every week for the current browser.
 */
export const Confetti = () => {
  const pathname = usePathname();

  const cookieBannerIsClosed = useCookieBannerClosed();
  const confettiSeenInLast7Days = hasBeenSeen(15); // fire confetti only once a week

  // no config for pathnames needed, as this feature is only for baur
  const onAllowedPage = !pathname?.includes('service-hilfe');

  useEffect(() => {
    // trigger confetti on first allowed page load session start and after cookie banner is closed
    if (isOnPageLoad() && onAllowedPage && cookieBannerIsClosed) {
      if (!confettiSeenInLast7Days) {
        fireworks();
        setSeen();
      }
      setConfettiSessionLock();
    }
  }, [confettiSeenInLast7Days, pathname, onAllowedPage, cookieBannerIsClosed]);

  return <span />;
};
